import {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react"

import { App } from "@/atoms"
import { useServices } from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { useKai } from "../KaiChat"
import * as taskActions from "./Task.actions"
import * as OpenPrTask from "./tasks/open-pr"

import styles from "./TaskProvider.module.scss"

import type { State } from "."

export const TaskContext = createContext<
  readonly [
    state: State,
    actions: { createPrTask: (payload: OpenPrTask.Payload) => void }
  ]
>([{}, { createPrTask: () => {} }])

export const TaskProvider = ({ children }: PropsWithChildren) => {
  const { http, logger } = useServices()
  const { notification } = App.useApp()
  const { open } = useKai()
  const { t } = useTranslation()
  const [state, setState] = useState<State>({})
  const globalAbortController = useRef<AbortController>()

  if (!globalAbortController.current)
    globalAbortController.current = new AbortController()

  const actions = useMemo(() => {
    const { signal: globalAbortSignal } = globalAbortController.current || {}

    return {
      createPrTask: (payload: OpenPrTask.Payload) => {
        const { fixId: id } = payload
        const task = new OpenPrTask.Task(payload, { http, globalAbortSignal })
        const actions = {
          t,
          logger,
          openNotification: taskActions.openNotification(id, notification),
          closeNotification: taskActions.closeNotification(id, notification)
        }

        task.onStateChange(taskActions.updateState(id, setState))
        task.onStateChange(OpenPrTask.notificationOnStateChange(actions))
        task.onStateChange(OpenPrTask.analyticsOnStateChange(actions))
        task.start()
      }
    }
  }, [http, logger, notification, t])

  const value = useMemo(() => [state, actions] as const, [state, actions])

  useEffect(() => {
    return () => {
      notification.destroy()
      globalAbortController.current?.abort()
      globalAbortController.current = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const className = styles.notificationKai
    const bodyClassList = document.body.classList

    if (!bodyClassList) return

    if (open) bodyClassList.add(className)
    else bodyClassList.remove(className)

    return () => bodyClassList.remove(className)
  }, [open])

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>
}
